<template>
  <div class="programs">
    <v-overlay :value="loading" z-index="4">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div v-if="!isCompanyAdmin" class="programs__unauthorized">
      <v-alert type="error">
        You don't have permission to access this page.
      </v-alert>
    </div>

    <div v-else>
      <div class="programs__header">
        <h1>Programs</h1>
        <div class="programs__header-actions">
          <v-switch
            v-model="showInactive"
            label="Show inactive"
            color="accent"
            class="programs__show-inactive"
          ></v-switch>
          <v-btn
            rounded
            color="accent"
            elevation="0"
            @click="openCreateDialog"
            class="programs__add-button v-btn"
          >
            <v-icon left>bhi-plus</v-icon>
            Add Program
          </v-btn>
        </div>
      </div>

      <programs-table
        :items="filteredPrograms"
        :loading="loading"
        @edit="openEditDialog"
        @delete="confirmDelete"
        @toggle-status="toggleProgramStatus"
      />

      <program-dialog
        v-model="dialog"
        :edited-index="editedIndex"
        :edited-item="editedItem"
        @close="closeDialog"
        @save="save"
      />

      <delete-program-dialog
        v-model="deleteDialog"
        @close="deleteDialog = false"
        @confirm="handleDeleteProgram"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import ProgramDialog from '@/components/programs/ProgramDialog.vue'
import DeleteProgramDialog from '@/components/programs/DeleteProgramDialog.vue'
import ProgramsTable from '@/components/programs/ProgramsTable.vue'

export default {
  name: 'PracticePrograms',
  components: {
    ProgramDialog,
    DeleteProgramDialog,
    ProgramsTable,
  },
  data() {
    return {
      dialog: false,
      deleteDialog: false,
      showInactive: false,
      editedIndex: -1,
      editedItem: {
        name: '',
        description: '',
        active: true,
      },
      defaultItem: {
        name: '',
        description: '',
        active: true,
      },
      programToDelete: null,
    }
  },
  computed: {
    ...mapState('programsStates', ['programs', 'loading']),
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('dictionariesStates', ['dictionaryPracticeUserRole']),
    isCompanyAdmin() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Administrator
      )
    },
    filteredPrograms() {
      if (this.showInactive) {
        return this.programs
      }
      return this.programs.filter(program => program.active)
    },
  },
  watch: {
    dialog(val) {
      val || this.closeDialog()
    },
    showInactive() {
      this.getPrograms({
        companyId: this.$route.params.id,
        active: this.showInactive ? undefined : true,
      })
    },
  },
  mounted() {
    if (this.isCompanyAdmin) {
      this.getPrograms({
        companyId: this.$route.params.id,
        active: true,
      })
    }
  },
  methods: {
    ...mapActions('programsStates', [
      'getPrograms',
      'createProgram',
      'updateProgram',
      'deleteProgram',
    ]),

    openCreateDialog() {
      this.editedIndex = -1
      this.editedItem = Object.assign({}, this.defaultItem)
      this.dialog = true
    },

    openEditDialog(item) {
      this.editedIndex = this.programs.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },

    closeDialog() {
      this.dialog = false
      this.$nextTick(() => {
        this.editedIndex = -1
        this.editedItem = Object.assign({}, this.defaultItem)
      })
    },

    async save() {
      if (this.editedIndex > -1) {
        const item = this.programs[this.editedIndex]
        await this.updateProgram({
          id: item.id,
          name: this.editedItem.name,
          description: this.editedItem.description,
          active: this.editedItem.active,
        })
      } else {
        await this.createProgram({
          name: this.editedItem.name,
          description: this.editedItem.description,
        })
      }
      this.closeDialog()
      this.getPrograms({
        companyId: this.$route.params.id,
        active: this.showInactive ? undefined : true,
      })
    },

    confirmDelete(item) {
      this.programToDelete = item
      this.deleteDialog = true
    },

    async handleDeleteProgram() {
      if (this.programToDelete) {
        await this.deleteProgram({ id: this.programToDelete.id })
        this.deleteDialog = false
        this.programToDelete = null
        this.getPrograms({
          companyId: this.$route.params.id,
          active: this.showInactive ? undefined : true,
        })
      }
    },

    async toggleProgramStatus(item) {
      await this.updateProgram({
        id: item.id,
        name: item.name,
        description: item.description,
        active: !item.active,
      })
      this.getPrograms({
        companyId: this.$route.params.id,
        active: this.showInactive ? undefined : true,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.programs {
  padding: 32px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    &-actions {
      display: flex;
      align-items: center;
      gap: 16px;
    }
  }

  &__add-button {
    margin-left: 16px;
  }

  &__show-inactive {
    margin-right: 16px;
  }

  &__unauthorized {
    padding: 24px;
  }
}
</style>
